import { captureExceptionWithOptions } from '@porterin/component-library/src/ts-utils/analytics/sentry/Sentry';

type ScriptStatus = 'idle' | 'loading' | 'loaded' | 'error';

const SCRIPT_ID = 'google-maps-api-script';

class GoogleMapsLoader {
  private static instance: GoogleMapsLoader;
  private scriptStatus: ScriptStatus = 'idle';
  private loadingPromise: Promise<void> | null = null;
  private libraries: string[] = ['places'];

  private constructor() {}

  static getInstance(): GoogleMapsLoader {
    if (!GoogleMapsLoader.instance) {
      GoogleMapsLoader.instance = new GoogleMapsLoader();
    }
    return GoogleMapsLoader.instance;
  }

  // Method to reset instance for testing
  static resetInstance(): void {
    GoogleMapsLoader.instance = new GoogleMapsLoader();
  }

  private loadScript(
    apiKey: string,
    maxRetries: number,
    retryTimeout: number,
    libraries: string[],
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const attemptLoad = (attempt: number) => {
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        const librariesParam = libraries.length > 0 ? `&libraries=${libraries.join(',')}` : '';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}${librariesParam}&loading=async`;
        script.async = true;
        script.defer = true;

        script.onload = () => {
          // Wait for Google Maps to be fully loaded with places library
          this.waitForGoogleMapsAndPlaces()
            .then(() => {
              this.scriptStatus = 'loaded';
              resolve();
            })
            .catch(() => {
              this.scriptStatus = 'error';
              captureExceptionWithOptions(
                'Google Maps Places library not available after script load',
                {
                  "Page": window?.location.pathname,
                }
              );
              reject(new Error('Google Maps Places library not loaded'));
            });
        };

        script.onerror = () => {
          document.head.removeChild(script);
          if (attempt < maxRetries) {
            setTimeout(() => attemptLoad(attempt + 1), retryTimeout);
          } else {
            this.scriptStatus = 'error';
            captureExceptionWithOptions(
              `Google Maps Script Load Failed after ${attempt} attempts`,
                {
                  "Page": window?.location.pathname,
                }
            );
            reject(new Error('Failed to load Google Maps script'));
          }
        };

        document.head.appendChild(script);
      };

      attemptLoad(1);
    });
  }

  private waitForGoogleMapsAndPlaces(): Promise<void> {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max wait

      const checkLibraries = () => {
        attempts++;

        if (window.google?.maps?.places?.Autocomplete) {
          resolve();
          return;
        }

        if (attempts >= maxAttempts) {
          reject(new Error('Google Maps Places library not loaded'));
          return;
        }

        setTimeout(checkLibraries, 100);
      };

      checkLibraries();
    });
  }

  loadApi(
    apiKey: string,
    maxRetries = 3,
    retryTimeout = 100,
    libraries: string[] = ['places'],
  ): Promise<void> {
    if (typeof window === 'undefined') {
      return Promise.reject(new Error('Cannot load Google Maps on the server'));
    }

    // Store libraries for this load
    this.libraries = libraries;

    if (window.google?.maps?.places?.Autocomplete) {
      this.scriptStatus = 'loaded';
      return Promise.resolve();
    }

    if (this.scriptStatus === 'loaded') {
      return Promise.resolve();
    }

    if (this.scriptStatus === 'loading' && this.loadingPromise) {
      return this.loadingPromise;
    }

    this.scriptStatus = 'loading';
    this.loadingPromise = this.loadScript(apiKey, maxRetries, retryTimeout, libraries);
    return this.loadingPromise;
  }
}

// Export the singleton instance method for easy use
export const loadGoogleMapsApi = (
  apiKey: string,
  maxRetries = 3,
  retryTimeout = 100,
  libraries: string[] = ['places'],
): Promise<void> => {
  return GoogleMapsLoader.getInstance().loadApi(
    apiKey,
    maxRetries,
    retryTimeout,
    libraries,
  );
};

// Export the class for testing
export { GoogleMapsLoader };
